import clsx from 'clsx';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../icons/Icon.js';
import type { IconName } from '../icons/registry.js';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

const ALERT_ICON: Record<AlertTone, IconName> = {
  info: 'info',
  warning: 'warning',
  danger: 'error',
  success: 'success',
};

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  icon?: IconName;
  className?: string;
}

export function Alert({ tone = 'info', title, children, icon, className }: AlertProps) {
  return (
    <div className={clsx('tb-alert', `tb-alert--${tone}`, className)} role="note">
      <Icon name={icon ?? ALERT_ICON[tone]} size={18} />
      <div className="tb-alert__body">
        {title ? <span className="tb-alert__title">{title}</span> : null}
        {children}
      </div>
    </div>
  );
}

export type BadgeTone = 'neutral' | 'live' | 'pending' | 'failed' | 'accent';

export interface BadgeProps {
  tone?: BadgeTone;
  icon?: IconName;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = 'neutral', icon, children, className }: BadgeProps) {
  return (
    <span className={clsx('tb-badge', tone !== 'neutral' && `tb-badge--${tone}`, className)}>
      {icon ? <Icon name={icon} size={13} /> : null}
      {children}
    </span>
  );
}

export interface EmptyStateProps {
  icon: IconName;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ icon, title, description, action }: EmptyStateProps) {
  return (
    <div className="tb-empty">
      <span className="tb-empty__icon">
        <Icon name={icon} size={28} />
      </span>
      <div className="tb-stack" style={{ gap: 4 }}>
        <span className="tb-title">{title}</span>
        {description ? <span className="tb-subtitle">{description}</span> : null}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ size = 20, label }: { size?: number; label?: string }) {
  return <Icon name="spinner" size={size} spin label={label} />;
}

export function Skeleton({
  height = 16,
  width,
  style,
}: {
  height?: number | string;
  width?: number | string;
  style?: CSSProperties;
}) {
  return <div className="tb-skeleton" style={{ height, width: width ?? '100%', ...style }} />;
}
